import { Columns3, Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Input } from '../../primitives/input';
import { cn } from '../../utils';
import type { ColumnDef, Density } from './types';

export interface DataTableToolbarProps {
  search: string;
  onSearchChange: (v: string) => void;
  searchPlaceholder?: string;
  density: Density;
  onDensityChange: (d: Density) => void;
  total: number;
  filtered: number;
  className?: string;
  /** Extra actions rendered in zone #4 (right-side of toolbar). */
  rightSlot?: React.ReactNode;
  /** Number of active column filters. Shows a "clear all" badge when > 0. */
  activeFilterCount?: number;
  onClearAllFilters?: () => void;
  /** All column definitions — used by column picker. If omitted, picker is hidden. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allColumns?: ColumnDef<any>[];
  /** Currently hidden column ids. */
  hiddenColumnIds?: ReadonlySet<string>;
  onToggleColumnVisibility?: (columnId: string) => void;
}

const DENSITY_OPTIONS: Array<{ id: Density; label: string }> = [
  { id: 'compact', label: 'კომპაქტი' },
  { id: 'comfortable', label: 'სტანდარტი' },
  { id: 'spacious', label: 'ფართო' },
];

/**
 * 4-zone sub-header per DESIGN_RULES §5:
 *   zone #1 (title block)  · zone #2 (stats)  · zone #3 (search+filters)  · zone #4 (actions)
 *
 * This toolbar renders zones #2 + #3 + #4. Zone #1 is the page's own header.
 */
export function DataTableToolbar({
  search,
  onSearchChange,
  searchPlaceholder = 'ძებნა…',
  density,
  onDensityChange,
  total,
  filtered,
  className,
  rightSlot,
  activeFilterCount = 0,
  onClearAllFilters,
  allColumns,
  hiddenColumnIds,
  onToggleColumnVisibility,
}: DataTableToolbarProps) {
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!columnPickerOpen) return;
    function handleOutside(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setColumnPickerOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [columnPickerOpen]);

  return (
    <div
      className={cn(
        'flex items-center gap-3 flex-wrap',
        'px-3 py-2 border-b border-[var(--color-border)]',
        'bg-[var(--color-bg)]',
        className,
      )}
    >
      {/* Zone #2 — stats */}
      <div className="flex items-center gap-1 text-[12px] text-[var(--color-text-secondary)] tabular-nums">
        <span className="font-medium text-[var(--color-text-primary)]">{filtered}</span>
        {filtered !== total ? (
          <>
            <span className="text-[var(--color-text-tertiary)]">/</span>
            <span>{total}</span>
          </>
        ) : null}
        <span className="text-[var(--color-text-tertiary)] ml-1">row{total === 1 ? '' : 's'}</span>
      </div>

      {/* Zone #3 — search */}
      <div className="relative flex-1 min-w-[180px] max-w-[420px]">
        <Search
          className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-[var(--color-text-tertiary)]"
          aria-hidden
        />
        <Input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.currentTarget.value)}
          placeholder={searchPlaceholder}
          className="pl-8 h-8 text-[13px]"
          aria-label={searchPlaceholder}
        />
      </div>

      {/* Zone #3 — density toggle */}
      <fieldset
        aria-label="density"
        className="inline-flex rounded-[4px] border border-[var(--color-border)] p-[2px] m-0"
      >
        {DENSITY_OPTIONS.map((d) => {
          const active = d.id === density;
          return (
            <label
              key={d.id}
              className={cn(
                'h-6 px-2 rounded-[2px] text-[11px] font-medium transition-colors cursor-pointer',
                'inline-flex items-center',
                active
                  ? 'bg-[var(--color-accent)] text-[var(--color-bg)]'
                  : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)]',
              )}
            >
              <input
                type="radio"
                name="datatable-density"
                value={d.id}
                checked={active}
                onChange={() => onDensityChange(d.id)}
                className="sr-only"
              />
              {d.label}
            </label>
          );
        })}
      </fieldset>

      {/* Zone #3 — active column filter badge */}
      {activeFilterCount > 0 ? (
        <button
          type="button"
          onClick={onClearAllFilters}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-[4px] border border-[var(--color-border)] text-[12px] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
        >
          <span className="font-medium text-[var(--color-accent)]">{activeFilterCount}</span>
          <span>filter{activeFilterCount === 1 ? '' : 's'}</span>
          <X className="size-3 ml-0.5" aria-hidden />
        </button>
      ) : null}

      {/* Zone #4 — column picker + extra actions */}
      <div className="flex items-center gap-2 ml-auto">
        {allColumns && allColumns.length > 0 && onToggleColumnVisibility ? (
          <div className="relative" ref={pickerRef}>
            <button
              type="button"
              aria-label="Toggle column visibility"
              aria-expanded={columnPickerOpen}
              onClick={() => setColumnPickerOpen((v) => !v)}
              className={cn(
                'inline-flex items-center gap-1.5 h-7 px-2 rounded-[4px]',
                'border border-[var(--color-border)] text-[12px]',
                'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                'transition-colors',
                columnPickerOpen && 'bg-[var(--color-surface)]',
              )}
            >
              <Columns3 className="size-3.5" aria-hidden />
              <span>Columns</span>
            </button>
            {columnPickerOpen ? (
              <div
                role="dialog"
                aria-label="Column visibility"
                className={cn(
                  'absolute right-0 top-full mt-1 z-50 min-w-[160px]',
                  'bg-[var(--color-bg)] border border-[var(--color-border)] rounded-[6px]',
                  'shadow-[0_4px_12px_rgba(0,0,0,0.12)] p-1',
                )}
              >
                {allColumns.map((col) => {
                  const colId = col.id ?? String((col as Record<string, unknown>).key ?? '');
                  const label = col.label ?? (typeof col.header === 'string' ? col.header : colId);
                  const isVisible = !(hiddenColumnIds?.has(colId) ?? false);
                  return (
                    <label
                      key={colId}
                      className={cn(
                        'flex items-center gap-2 px-2 py-1.5 rounded-[4px] cursor-pointer',
                        'text-[12px] text-[var(--color-text-primary)]',
                        'hover:bg-[var(--color-surface)] transition-colors',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isVisible}
                        onChange={() => onToggleColumnVisibility(colId)}
                        className="accent-[var(--color-accent)]"
                      />
                      {label}
                    </label>
                  );
                })}
              </div>
            ) : null}
          </div>
        ) : null}
        {rightSlot}
      </div>
    </div>
  );
}
